import { DataGrid, GridColDef, GridRowModel, GridRowModesModel } from "@mui/x-data-grid"
import { useContext, useEffect, useState } from "react"
import { ModalContext } from "../../../providers/modal.provider"
import { columnToTitle, count, validateEid } from "../../../helpers/util.helper"
import { Box, Button, TextField, Tooltip } from "@mui/material"
import { CattleRecordFormSingleViewComponent } from "../cattle-records/cattle-form-single-record-view.component"
import { IconAdmin } from "../../ui/buttons/button-admin"
import { isAdmin } from "../../../helpers/account"
import { Herd, HerdAttribute } from "../../../services/herd-groups"
import { CattleFormSingleViewDto } from "../../../dto/animal-record"
import { IHerdAttribute, IHerdAttributeResponse } from "../../../interfaces/herd-cattle.interface"
import { ComponentStateDto } from "../../../interfaces/component-state.interface"
import { BootStrapSpinner } from "../../ui/views/spinner"
import { ipfsUri } from "../../../helpers/ipfs.helper"
import { cacheGet } from "../../../services/cache"
import { filterMuiColumns } from "../../../helpers/arrayworks.helper"
import OutputIcon from '@mui/icons-material/Output'
import ChangeCircleIcon from '@mui/icons-material/ChangeCircle';
import { ApplicationContext } from "../../../providers/application.provider"
import { groupStatusEnum } from "../../../enums/group.status.enum"
import { returnHerdColumns } from "."
//import { groupStatusEnum } from "../../../enums/group.status.enum"

interface IAnimalEditorGridComponent
{
    isEditable: () => boolean
    setUpdate: (update: boolean) => void
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    setHerd: (herd: any) => void
    gid: string
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    herd: any
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    herdState: any
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    update: any
    setRefreshHerd: (refresh: boolean) => void
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    setGroupsState: (state: any) => void
    setGid?: (gid: string) => void
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    setHerdState?: (state: any) => void
    groupId?: string
    groupStatus?: number
    rowCount?: number
}

export const AnimalEditorGridComponent = ({ 
    isEditable,
    setUpdate,
    setHerd,
    gid,
    herd,
    rowCount,
    herdState,
    update,
    setRefreshHerd,
    setGroupsState,
    groupStatus
}: IAnimalEditorGridComponent) => {
    const [ search, setSearch ] = useState<string>('')
    const [ rowModesModel, setRowModesModel ] = useState<GridRowModesModel>({});
    const [ dataTableTarget, setDataTableTarget ] = useState('')
    const [ uploadNew ] = useState(false)
    const [ searched, setSearched ] = useState<boolean>(false)
    const [ newAnimal, setNewAnimal ] = useState<boolean>(false)
    const [ reload, setReload ] = useState<boolean>(false)
    const [ totalCount, setTotalCount ] = useState<number>(rowCount || 0);
    const [ thinking, setThinking ] = useState<boolean>(false);
    const [ paginationModel, setPaginationModel ] = useState({ page: 0, pageSize: 25 });

    const { setModalActive, setModalBody, setModalTitle, setModalEvent } = useContext(ModalContext)
    const { breedState } = useContext(ApplicationContext);

    const gidLocked: boolean = !isEditable()
    const dateWidth = 120
    
    const dataTableColumns: GridColDef[] = [
        // {field: 'id', headerName: 'ID' },
        // {field: 'gid', headerName: 'Group Id' },
        { field: 'ranch_name', headerName: 'Ranch', flex: 1, minWidth: 250, editable: false },
        { field: 'location', headerName: 'Location', width: dateWidth, editable: false },
        { field: 'archived_at', headerName: 'Disposed', width: dateWidth, editable: false, renderCell: (e: any) => e.row.is_disposed || e.row?.animal_disposed_at? <OutputIcon color={ e.row.is_disposed? 'error' : 'inherit'  } /> : '' },
        { field: 'breed', headerName: 'Breed', width: dateWidth, editable: false, renderCell: (e: {row: {breed: string}}) =>  columnToTitle(e.row.breed) },
        { field: 'percent_angus', headerName: '% Angus', width: 90, align: 'center', editable: false, renderCell: (e: any) => {
            return (
                e.row.percent_angus? <Tooltip title={ `Percent Angus: ${e.row.percent_angus}%` }><span style={{ fontWeight: 'bold', color: e.row.percent_angus > 50 ? 'green' : 'red' }}>{ e.row.percent_angus }%</span></Tooltip> : ''
            )
        }},
        { field: 'eid', headerName: 'PCT Id', flex: 1, minWidth: 250, editable: !gidLocked, preProcessEditCellProps: validateEid, renderCell: (e: any) => e.row?.has_replacement_tag || e.row?.tag_exchange_in_progress === 1? <Tooltip title={ `Pending change` }><span>{ e.row.eid }<ChangeCircleIcon fontSize="small" className="fx-infinite-rotate" /></span></Tooltip> : e.row.eid },
        { field: 'visual_tag', headerName: 'Non-PCT Id', flex: 1, minWidth: 250, editable: !gidLocked },
        { field: 'tag_color', headerName: 'Tag Color', minWidth: 80, editable: !gidLocked },
        { field: 'death_date', headerName: 'Deceased', cellClassName: 'd-flex justify-content-center align-items-center', minWidth: 80, editable: !gidLocked, renderCell: (e: any) => e.row.death_date? <i className="fas fa-skull-crossbones"></i> : null },
        { field: 'is_available', headerName: 'Tag Status', minWidth: 80, renderCell: (e: any) => <Tooltip title={!e.value? "You have not supplied evidence of ownership or it has not been verified yet by CattleProof." : 'Verified'}><div className={`badge bkg-${!e.value? 'red' : 'green'}` }>{!e.value && 'UN'}VERIFIED</div></Tooltip> },
        { field: 'cid', headerName: 'CID', minWidth: 40, editable: false, renderCell: (e: any) => {
            return (
                e.value? <a href={ipfsUri(e.value)} target="_blank" rel="no-referrer">IPFS</a> : ''
            )
        } },
        { field: 'action', headerName: 'Action', width: 80, editable: false, renderCell: (e: any) => {
            return (
                e.row.is_disposed || e.row?.animal_disposed_at? '' : (
                    <Button
                        disabled={ ![groupStatusEnum.ready, groupStatusEnum.approved ].includes(groupStatus || groupStatusEnum.ready) || e.row?.tag_exchange_in_progress === 1 }
                        onClick={() => {
                            setModalActive(true)
                            setModalTitle('Edit Animal')
                            if(setModalEvent)
                                setModalEvent(() => setUpdate)
                            setModalBody(
                                <CattleRecordFormSingleViewComponent
                                    reloadHerd={ setUpdate }
                                    animal={ e.row }
                                    setUpdate={ setUpdate }
                                />
                            )
                        }
                    }>
                        { isAdmin()? <IconAdmin text='' /> : <i className="fas fa-edit"></i>}
                    </Button>
                )
            )}
        }
    ]

    const submitSearch = (search?: string) => {
        Herd(gid, search).then(r => {
            // set the rows
            if(r.success) {
                // Set the rows
                setHerd(r.herd || []);
                // Set the total count if available
                if(r.count !== undefined) {
                    setTotalCount(r.count);
                }
            }
        })
    }

    const tableView = ((herdState.ready && herd.length > 0) || search) && (!update && !uploadNew)
    
    // Update single row/column attributes
    const updateAttribute = ({gid, rowId, field, value}: IHerdAttribute) => HerdAttribute<IHerdAttributeResponse>(gid, rowId, field, value);

    if(tableView && searched && search === '') {
        setRefreshHerd(true);
        setSearched(false);
    }
    
    if(update) {
        setGroupsState({...ComponentStateDto, completed: false});
        setRefreshHerd(true);
        setUpdate(false);
    }

    const onCloseOutAddAnimalEvent = () => {
        setRefreshHerd(true);
        setNewAnimal(false);
    }
    
    useEffect(() => {
        if(reload) {
            setRefreshHerd(true);
            setReload(false);
        }
    }, [ reload ])

    useEffect(() => {
        if(newAnimal) {
            setModalBody(<CattleRecordFormSingleViewComponent
                isNew={true}
                reloadHerd={ onCloseOutAddAnimalEvent }
                gid={gid}
                animal={ new CattleFormSingleViewDto({}) }
                setUpdate={setNewAnimal}
            />)
            setModalTitle("Create Animal");
            if(setModalEvent)
                setModalEvent(() => () => {
                    onCloseOutAddAnimalEvent();
                    setModalActive(false);
                })
            setModalActive(true);
        }
    }, [ newAnimal ])

    return (
        <div>
            <form onSubmit={(e) => {
                e.preventDefault();
                submitSearch(search);
            }}>
                <div className="d-flex gapped p-3 bkg-light-grey rounded border border-secondary mt-3 search-box-container ignore-nightshift">
                    <TextField value={search} label="Search Herd Group" onChange={e => setSearch(e.target.value)} className="ignore-nightshift" />
                    <Button variant="contained"  onClick={() => submitSearch(search)}><i className="fas fa-search"></i>Search</Button>
                    <Button variant="contained" disabled={search === ''} onClick={() => {
                        submitSearch('')
                        setSearch('')
                    }}><i className="fas fa-eraser"></i>Clear</Button>
                    { isEditable()? <Button variant="contained" onClick={() => setNewAnimal(true)}><i className="fas fa-plus"></i>Add Animal</Button> : null }
                    { count(herd) > 0? <a href={ `${import.meta.env.VITE_APIENDPOINT}/generate/csv/${gid}?jwtToken=${cacheGet('token')}&dl=1` } className="ignore-nightshift"><Button className="py-3" variant="outlined"><i className="fas fa-file-download"></i>&nbsp;Download Animals</Button></a> : null }
                </div>
            </form>

            <div className="mt-3" style={ { height: 'auto' } }>
                { breedState?.ready? 
                <DataGrid
                    getRowId={ e => e.ID }
                    paginationMode="server"
                    paginationModel={paginationModel}
                    rowCount={totalCount}
                    onPaginationModelChange={ (newModel) => {
                        setPaginationModel(newModel);
                        const { pageSize, page } = newModel;
                        setThinking(true);
                        Herd(gid, search, page, pageSize, returnHerdColumns).then(r => {
                            setThinking(false);
                            if(r.success) {
                                setHerd(r.herd || []);
                                // Set the total count if available
                                if(r.count !== undefined) {
                                    setTotalCount(r.count);
                                }
                            }
                        }
                    ) }}
                    //onProcessRowUpdateError={(e) => e}
                    rowModesModel={ rowModesModel }
                    onRowModesModelChange={ setRowModesModel }
                    onCellEditStart={(e) => setDataTableTarget(e.field)}
                    processRowUpdate={(e: GridRowModel) => {
                        updateAttribute({
                            field: dataTableTarget,
                            gid: gid,
                            rowId: e.ID,
                            value: e[dataTableTarget as keyof typeof e]
                        })
                        return e
                    }}
                    rows={ herd }
                    columns={ filterMuiColumns<any>(dataTableColumns, herd, ['action', 'is_available']) }
                    checkboxSelection={false}
                /> : <BootStrapSpinner /> }
                { thinking && <Box sx={{position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0) !important', backdropFilter: 'blur(5px)'}} className="d-flex justify-content-center align-items-center"><BootStrapSpinner /></Box> }
            </div>
        </div>
    )
}